import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Save, Eye, RotateCcw, Download, Move, Type, Palette } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// PDF'de kullanılabilir fontlar - Backend'de TTF olarak kayıtlı olanlar
const AVAILABLE_FONTS = [
  // El Yazısı / Script Fontlar (PDF'de çalışıyor)
  'Great Vibes',
  'Dancing Script',
  'Pacifico',
  'Allura',
  'Alex Brush',
  'Kaushan Script',
  // Klasik / Serif Fontlar
  'Playfair Display',
  // Sans Serif Fontlar
  'Montserrat',
];

// Varsayılan tasarım ayarları - tüm öğeler için X ve Y
const DEFAULT_DESIGN = {
  // İsim ayarları
  nameFont: 'Playfair Display',
  nameFontSize: 52,
  nameColor: '#1a365d',
  nameX: 421, // center = 842/2
  nameY: 250,
  
  // "This Certificate is Proudly Presented To" metni
  presentedFont: 'Helvetica',
  presentedFontSize: 14,
  presentedColor: '#3a3a3a',
  presentedX: 421,
  presentedY: 185,
  presentedText: 'This Certificate is Proudly Presented To',
  
  // Program açıklaması
  descriptionFont: 'Helvetica',
  descriptionFontSize: 13,
  descriptionColor: '#3a3a3a',
  descriptionX: 421,
  descriptionY: 310,
  descriptionText1: 'For successfully completing the',
  descriptionText2: 'in accordance with the established curriculum, assessment, and evaluation criteria.',
  descriptionText3: 'The recipient has demonstrated a satisfactory level of performance in the examinations,',
  descriptionText4: 'practical applications, and evaluations conducted throughout the program',
  descriptionText5: 'and has met the required standards of achievement.',
  
  // Detaylı açıklama (3-5. satırlar) font ayarları
  descDetailFont: 'Helvetica',
  descDetailFontSize: 11,
  descDetailColor: '#5a5a5a',
  
  // Program detayları
  detailsTitleFont: 'Montserrat',
  detailsTitleFontSize: 11,
  detailsTitleColor: '#1a365d',
  detailsX: 180,
  detailsY: 155, // alttan mesafe
  detailsTitleText: 'PROGRAM DETAILS',
  
  detailsFont: 'Helvetica',
  detailsFontSize: 11,
  detailsColor: '#3a3a3a',
  
  // Sağ sütun detaylar
  detailsRightX: 520,
  
  // İmza bölümü
  signatureFont: 'Montserrat',
  signatureFontSize: 12,
  signatureColor: '#1a1a1a',
  signatureX: 220,
  signatureY: 100,
  signatureTitle: 'Education Director',
  
  // QR kod
  qrX: 662, // 842 - 180
  qrY: 110,
  qrSize: 60,
  qrText: 'Scan to Verify',
  
  // Disclaimer
  disclaimerX: 421,
  disclaimerY: 48,
  disclaimerText1: 'LEGAL DISCLAIMER: This certificate confirms the successful completion of the stated training program.',
  disclaimerText2: 'It does not constitute a professional license, authorization, or guarantee of employment.',
  
  // Altın çizgi
  goldLineColor: '#b8860b',
  goldLineWidth: 2.5,
  
  // Footer
  footerY: 25
};

export default function CertificateDesigner() {
  const [design, setDesign] = useState(DEFAULT_DESIGN);
  const [loading, setLoading] = useState(false);
  const [activeElement, setActiveElement] = useState('name');
  const canvasRef = useRef(null);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  
  // Örnek veriler - tarihler sayısal formatta
  const sampleData = {
    fullName: 'John Alexander Smith',
    programName: 'Aviation English Level B2',
    programDuration: '120',
    startDate: '01.10.2025',
    endDate: '31.12.2025',
    certificateId: 'cc-sky50101',
    issueDate: '02.01.2026',
    authName: 'Dr. Sarah Johnson',
    authTitle: 'Education Director'
  };

  // Google Fonts yükle (canvas önizleme için)
  useEffect(() => {
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${AVAILABLE_FONTS.map(f => f.replace(' ', '+')).join('&family=')}&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    link.onload = () => setFontsLoaded(true);
    return () => { document.head.removeChild(link); };
  }, []);

  // Canvas önizleme çiz
  useEffect(() => {
    if (!fontsLoaded) return;
    drawPreview();
  }, [design, fontsLoaded, activeElement]);

  // Kayıtlı tasarımı yükle
  useEffect(() => {
    fetchDesign();
  }, []);

  const fetchDesign = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/certificate-design`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.design) {
          setDesign({ ...DEFAULT_DESIGN, ...data.design });
        }
      }
    } catch (error) {
      console.log('No saved design found');
    }
  };

  const drawPreview = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const w = 842;
    const h = 595;
    
    canvas.width = w;
    canvas.height = h;
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    
    const bgImg = new Image();
    bgImg.crossOrigin = 'anonymous';
    bgImg.src = `${API_URL}/api/certificate-background`;
    
    bgImg.onload = () => {
      ctx.drawImage(bgImg, 0, 0, w, h);
      drawAllElements(ctx, w, h);
    };
    
    bgImg.onerror = () => {
      ctx.strokeStyle = design.goldLineColor;
      ctx.lineWidth = 3;
      ctx.strokeRect(20, 20, w - 40, h - 40);
      drawAllElements(ctx, w, h);
    };
  };

  const drawAllElements = (ctx, w, h) => {
    // Aktif element için highlight kutusu çiz
    const highlightElement = (x, y, width, height) => {
      ctx.save();
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(x - width/2 - 5, y - height - 5, width + 10, height + 10);
      ctx.restore();
    };

    // "This Certificate is Proudly Presented To"
    ctx.font = `${design.presentedFontSize}px ${design.presentedFont}`;
    ctx.fillStyle = design.presentedColor;
    ctx.textAlign = 'center';
    ctx.fillText('This Certificate is Proudly Presented To', design.presentedX, design.presentedY);
    if (activeElement === 'presented') {
      const tw = ctx.measureText('This Certificate is Proudly Presented To').width;
      highlightElement(design.presentedX, design.presentedY, tw, design.presentedFontSize);
    }
    
    // İsim
    ctx.font = `bold ${design.nameFontSize}px "${design.nameFont}"`;
    ctx.fillStyle = design.nameColor;
    ctx.fillText(sampleData.fullName, design.nameX, design.nameY);
    if (activeElement === 'name') {
      const tw = ctx.measureText(sampleData.fullName).width;
      highlightElement(design.nameX, design.nameY, tw, design.nameFontSize);
    }
    
    // İsim altı çizgi
    const nameWidth = ctx.measureText(sampleData.fullName).width;
    ctx.strokeStyle = design.goldLineColor;
    ctx.lineWidth = design.goldLineWidth;
    ctx.beginPath();
    ctx.moveTo(design.nameX - nameWidth/2 - 40, design.nameY + 22);
    ctx.lineTo(design.nameX + nameWidth/2 + 40, design.nameY + 22);
    ctx.stroke();
    
    // Program açıklaması
    ctx.font = `${design.descriptionFontSize}px ${design.descriptionFont}`;
    ctx.fillStyle = design.descriptionColor;
    ctx.fillText(`For successfully completing the ${sampleData.programName}`, design.descriptionX, design.descriptionY);
    ctx.fillText('in accordance with the established curriculum, assessment, and evaluation criteria.', design.descriptionX, design.descriptionY + 20);
    if (activeElement === 'description') {
      const tw = ctx.measureText(`For successfully completing the ${sampleData.programName}`).width;
      highlightElement(design.descriptionX, design.descriptionY, tw, design.descriptionFontSize * 2);
    }
    
    // Detaylı açıklama
    ctx.font = `11px ${design.descriptionFont}`;
    ctx.fillStyle = '#5a5a5a';
    ctx.fillText('The recipient has demonstrated a satisfactory level of performance in the examinations,', design.descriptionX, design.descriptionY + 50);
    ctx.fillText('practical applications, and evaluations conducted throughout the program', design.descriptionX, design.descriptionY + 68);
    ctx.fillText('and has met the required standards of achievement.', design.descriptionX, design.descriptionY + 86);
    
    // Program detayları başlığı - detailsY alttan mesafe
    const detailsDrawY = h - design.detailsY;
    ctx.font = `bold ${design.detailsTitleFontSize}px "${design.detailsTitleFont}"`;
    ctx.fillStyle = design.detailsTitleColor;
    ctx.textAlign = 'left';
    ctx.fillText('PROGRAM DETAILS', design.detailsX, detailsDrawY);
    if (activeElement === 'details') {
      highlightElement(design.detailsX + 60, detailsDrawY, 150, 60);
    }
    
    // Program detayları - başlığın altına doğru sıralı
    ctx.font = `${design.detailsFontSize}px ${design.detailsFont}`;
    ctx.fillStyle = design.detailsColor;
    ctx.fillText(`Program Duration: ${sampleData.programDuration} Hours`, design.detailsX, detailsDrawY + 22);
    ctx.fillText(`Certificate Number: ${sampleData.certificateId}`, design.detailsX, detailsDrawY + 42);
    ctx.fillText(`Issue Date: ${sampleData.issueDate}`, design.detailsX, detailsDrawY + 62);
    
    // İmza bölümü - Y değeri alttan mesafe olarak kullanılıyor
    const sigDrawY = h - design.signatureY; // Canvas'ta üstten hesapla
    ctx.textAlign = 'center';
    
    // İmza görseli placeholder (gerçek görsel backend'de)
    ctx.font = `italic 14px Arial`;
    ctx.fillStyle = '#8B4513';  // Kahverengi - imza rengi
    ctx.fillText('~ Signature ~', design.signatureX, sigDrawY - 35);
    
    // İmza çizgisi (gold)
    ctx.strokeStyle = design.goldLineColor;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(design.signatureX - 70, sigDrawY - 10);
    ctx.lineTo(design.signatureX + 70, sigDrawY - 10);
    ctx.stroke();
    
    // Unvan
    ctx.font = `11px ${design.detailsFont}`;
    ctx.fillStyle = design.detailsColor;
    ctx.fillText(sampleData.authTitle, design.signatureX, sigDrawY + 8);
    
    if (activeElement === 'signature') {
      highlightElement(design.signatureX, sigDrawY - 15, 160, 60);
    }
    
    // QR kod placeholder - Y değeri alttan mesafe
    const qrDrawY = h - design.qrY;
    ctx.strokeStyle = '#1a365d';
    ctx.lineWidth = 1;
    ctx.strokeRect(design.qrX - design.qrSize/2, qrDrawY - design.qrSize/2, design.qrSize, design.qrSize);
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(design.qrX - design.qrSize/2 + 1, qrDrawY - design.qrSize/2 + 1, design.qrSize - 2, design.qrSize - 2);
    ctx.fillStyle = '#666';
    ctx.font = '10px Helvetica';
    ctx.fillText('QR', design.qrX, qrDrawY + 4);
    
    ctx.font = '9px Helvetica';
    ctx.fillStyle = '#3a3a3a';
    ctx.fillText('Scan to Verify', design.qrX, qrDrawY - design.qrSize/2 - 8);
    ctx.font = 'bold 8px Montserrat';
    ctx.fillStyle = '#1a365d';
    ctx.fillText(sampleData.certificateId, design.qrX, qrDrawY + design.qrSize/2 + 15);
    
    if (activeElement === 'qr') {
      highlightElement(design.qrX, qrDrawY, design.qrSize + 20, design.qrSize + 40);
    }
    
    // Disclaimer - Y değeri alttan mesafe
    const disclaimerDrawY = h - design.disclaimerY;
    ctx.font = '7px Helvetica';
    ctx.fillStyle = '#5a5a5a';
    ctx.fillText('LEGAL DISCLAIMER: This certificate confirms the successful completion of the stated training program.', design.disclaimerX, disclaimerDrawY);
    ctx.fillText('It does not constitute a professional license, authorization, or guarantee of employment.', design.disclaimerX, disclaimerDrawY + 10);
    if (activeElement === 'disclaimer') {
      highlightElement(design.disclaimerX, disclaimerDrawY + 5, 500, 20);
    }
  };

  const handleDesignChange = (key, value) => {
    setDesign(prev => ({ ...prev, [key]: value }));
  };

  const saveDesign = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/certificate-design`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ design })
      });
      if (response.ok) {
        toast.success('Tasarım başarıyla kaydedildi!');
      } else {
        toast.error('Tasarım kaydedilemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
    setLoading(false);
  };

  const resetDesign = () => {
    setDesign(DEFAULT_DESIGN);
    toast.info('Varsayılan ayarlara döndürüldü');
  };

  const generatePreviewPDF = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('[DEBUG] Sending design to preview:', JSON.stringify(design, null, 2));
      const response = await fetch(`${API_URL}/api/admin/certificate-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ design, sampleData })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        toast.success('PDF önizleme oluşturuldu');
      } else {
        const error = await response.text();
        console.error('[DEBUG] Preview error:', error);
        toast.error('PDF oluşturulamadı');
      }
    } catch (error) {
      console.error('[DEBUG] Preview exception:', error);
      toast.error('Bir hata oluştu');
    }
    setLoading(false);
  };

  // Pozisyon kontrolü bileşeni
  const PositionControl = ({ label, xKey, yKey, xMin = 0, xMax = 842, yMin = 0, yMax = 595, yLabel = "Y (Üstten)" }) => (
    <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
      <h4 className="font-medium text-sm flex items-center gap-2">
        <Move className="w-4 h-4" />
        {label} Pozisyonu
      </h4>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">X (Yatay - soldan)</Label>
          <div className="flex gap-2 mt-1">
            <Slider
              value={[design[xKey]]}
              onValueChange={([v]) => handleDesignChange(xKey, v)}
              min={xMin}
              max={xMax}
              step={1}
              className="flex-1"
            />
            <Input
              type="number"
              value={design[xKey]}
              onChange={(e) => handleDesignChange(xKey, parseInt(e.target.value) || 0)}
              className="w-16 h-8 text-xs"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">{yLabel}</Label>
          <div className="flex gap-2 mt-1">
            <Slider
              value={[design[yKey]]}
              onValueChange={([v]) => handleDesignChange(yKey, v)}
              min={yMin}
              max={yMax}
              step={1}
              className="flex-1"
            />
            <Input
              type="number"
              value={design[yKey]}
              onChange={(e) => handleDesignChange(yKey, parseInt(e.target.value) || 0)}
              className="w-16 h-8 text-xs"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const FontControl = ({ label, fontKey, sizeKey, colorKey }) => (
    <div className="space-y-3 p-3 bg-blue-50 rounded-lg">
      <h4 className="font-medium text-sm flex items-center gap-2">
        <Type className="w-4 h-4" />
        {label} Font Ayarları
      </h4>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Font</Label>
          <Select value={design[fontKey]} onValueChange={(v) => handleDesignChange(fontKey, v)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Helvetica">Helvetica</SelectItem>
              {AVAILABLE_FONTS.map(font => (
                <SelectItem key={font} value={font} style={{ fontFamily: font }}>{font}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Boyut</Label>
          <Input
            type="number"
            value={design[sizeKey]}
            onChange={(e) => handleDesignChange(sizeKey, parseInt(e.target.value) || 12)}
            className="h-8 text-xs"
          />
        </div>
        <div>
          <Label className="text-xs">Renk</Label>
          <div className="flex gap-1">
            <Input
              type="color"
              value={design[colorKey]}
              onChange={(e) => handleDesignChange(colorKey, e.target.value)}
              className="w-8 h-8 p-0.5 cursor-pointer"
            />
            <Input
              type="text"
              value={design[colorKey]}
              onChange={(e) => handleDesignChange(colorKey, e.target.value)}
              className="flex-1 h-8 text-xs"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Element seçim butonları
  const ElementButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveElement(id)}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
        activeElement === id 
          ? 'bg-blue-600 text-white shadow-md' 
          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sertifika Tasarımcısı</h1>
          <p className="text-gray-500 text-sm">Öğeleri seçin ve konumlarını ayarlayın</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={resetDesign}>
            <RotateCcw className="w-4 h-4 mr-1" />
            Sıfırla
          </Button>
          <Button variant="outline" size="sm" onClick={generatePreviewPDF} disabled={loading}>
            <Download className="w-4 h-4 mr-1" />
            PDF Önizle
          </Button>
          <Button size="sm" onClick={saveDesign} disabled={loading}>
            <Save className="w-4 h-4 mr-1" />
            Kaydet
          </Button>
        </div>
      </div>

      {/* Element Seçim Butonları */}
      <div className="flex flex-wrap gap-2 p-3 bg-white rounded-lg shadow-sm">
        <span className="text-sm text-gray-500 self-center mr-2">Düzenle:</span>
        <ElementButton id="content" label="📝 İçerik" icon={<Type className="w-4 h-4" />} />
        <ElementButton id="presented" label="Takdim" icon={<Type className="w-4 h-4" />} />
        <ElementButton id="name" label="İsim" icon={<Type className="w-4 h-4" />} />
        <ElementButton id="description" label="Açıklama" icon={<Type className="w-4 h-4" />} />
        <ElementButton id="details" label="Detaylar" icon={<Type className="w-4 h-4" />} />
        <ElementButton id="signature" label="İmza" icon={<Type className="w-4 h-4" />} />
        <ElementButton id="qr" label="QR Kod" icon={<Move className="w-4 h-4" />} />
        <ElementButton id="disclaimer" label="Uyarı" icon={<Type className="w-4 h-4" />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Önizleme - Sol taraf (3/5) */}
        <Card className="lg:col-span-3">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Canlı Önizleme
              <span className="text-xs text-gray-400 ml-2">(Kırmızı çerçeve = seçili öğe)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="border rounded-lg overflow-hidden bg-gray-100">
              <canvas
                ref={canvasRef}
                className="w-full h-auto"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Ayarlar - Sağ taraf (2/5) */}
        <Card className="lg:col-span-2">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Seçili Öğe Ayarları</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
            {/* İçerik Düzenleme */}
            {activeElement === 'content' && (
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">📝 Takdim Metni</h4>
                  <Input
                    value={design.presentedText || ''}
                    onChange={(e) => handleDesignChange('presentedText', e.target.value)}
                    placeholder="This Certificate is Proudly Presented To"
                    className="text-sm"
                  />
                </div>
                
                <div className="p-3 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">📝 Açıklama Metinleri</h4>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-gray-500">1. Satır (program adından önce)</Label>
                      <Input
                        value={design.descriptionText1 || ''}
                        onChange={(e) => handleDesignChange('descriptionText1', e.target.value)}
                        placeholder="For successfully completing the"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">2. Satır (program adından sonra)</Label>
                      <Input
                        value={design.descriptionText2 || ''}
                        onChange={(e) => handleDesignChange('descriptionText2', e.target.value)}
                        placeholder="in accordance with..."
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">3. Satır</Label>
                      <Input
                        value={design.descriptionText3 || ''}
                        onChange={(e) => handleDesignChange('descriptionText3', e.target.value)}
                        placeholder="The recipient has demonstrated..."
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">4. Satır</Label>
                      <Input
                        value={design.descriptionText4 || ''}
                        onChange={(e) => handleDesignChange('descriptionText4', e.target.value)}
                        placeholder="practical applications..."
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">5. Satır</Label>
                      <Input
                        value={design.descriptionText5 || ''}
                        onChange={(e) => handleDesignChange('descriptionText5', e.target.value)}
                        placeholder="and has met the required..."
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">📝 Detaylar Başlığı</h4>
                  <Input
                    value={design.detailsTitleText || ''}
                    onChange={(e) => handleDesignChange('detailsTitleText', e.target.value)}
                    placeholder="PROGRAM DETAILS"
                    className="text-sm"
                  />
                </div>
                
                <div className="p-3 bg-purple-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">📝 İmza Unvanı</h4>
                  <Input
                    value={design.signatureTitle || ''}
                    onChange={(e) => handleDesignChange('signatureTitle', e.target.value)}
                    placeholder="Education Director"
                    className="text-sm"
                  />
                </div>
                
                <div className="p-3 bg-cyan-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">📝 QR Kod Metni</h4>
                  <Input
                    value={design.qrText || ''}
                    onChange={(e) => handleDesignChange('qrText', e.target.value)}
                    placeholder="Scan to Verify"
                    className="text-sm"
                  />
                </div>
                
                <div className="p-3 bg-red-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">📝 Uyarı Metinleri</h4>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-gray-500">1. Satır</Label>
                      <Input
                        value={design.disclaimerText1 || ''}
                        onChange={(e) => handleDesignChange('disclaimerText1', e.target.value)}
                        placeholder="LEGAL DISCLAIMER..."
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">2. Satır</Label>
                      <Input
                        value={design.disclaimerText2 || ''}
                        onChange={(e) => handleDesignChange('disclaimerText2', e.target.value)}
                        placeholder="It does not constitute..."
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Takdim Metni */}
            {activeElement === 'presented' && (
              <>
                <PositionControl label="Takdim Metni" xKey="presentedX" yKey="presentedY" yMin={20} yMax={550} />
                <FontControl label="Takdim Metni" fontKey="presentedFont" sizeKey="presentedFontSize" colorKey="presentedColor" />
              </>
            )}

            {/* İsim */}
            {activeElement === 'name' && (
              <>
                <PositionControl label="İsim" xKey="nameX" yKey="nameY" yMin={20} yMax={550} />
                <FontControl label="İsim" fontKey="nameFont" sizeKey="nameFontSize" colorKey="nameColor" />
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <h4 className="font-medium text-sm flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Alt Çizgi
                  </h4>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <Label className="text-xs">Renk</Label>
                      <div className="flex gap-1 mt-1">
                        <Input
                          type="color"
                          value={design.goldLineColor}
                          onChange={(e) => handleDesignChange('goldLineColor', e.target.value)}
                          className="w-8 h-8 p-0.5"
                        />
                        <Input
                          type="text"
                          value={design.goldLineColor}
                          onChange={(e) => handleDesignChange('goldLineColor', e.target.value)}
                          className="flex-1 h-8 text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Kalınlık</Label>
                      <Input
                        type="number"
                        value={design.goldLineWidth}
                        onChange={(e) => handleDesignChange('goldLineWidth', parseFloat(e.target.value) || 1)}
                        className="h-8 text-xs mt-1"
                        step="0.5"
                        min="0.5"
                        max="10"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Açıklama */}
            {activeElement === 'description' && (
              <>
                <PositionControl label="Açıklama" xKey="descriptionX" yKey="descriptionY" yMin={20} yMax={550} />
                <FontControl label="Ana Açıklama (1-2. satır)" fontKey="descriptionFont" sizeKey="descriptionFontSize" colorKey="descriptionColor" />
                <FontControl label="Detaylı Açıklama (3-5. satır)" fontKey="descDetailFont" sizeKey="descDetailFontSize" colorKey="descDetailColor" />
              </>
            )}

            {/* Detaylar */}
            {activeElement === 'details' && (
              <>
                <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-sm">Program Detayları Pozisyonu</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">X (Soldan)</Label>
                      <div className="flex gap-2 mt-1">
                        <Slider
                          value={[design.detailsX]}
                          onValueChange={([v]) => handleDesignChange('detailsX', v)}
                          min={50}
                          max={400}
                          step={5}
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          value={design.detailsX}
                          onChange={(e) => handleDesignChange('detailsX', parseInt(e.target.value) || 0)}
                          className="w-16 h-8 text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Y (Alttan mesafe)</Label>
                      <div className="flex gap-2 mt-1">
                        <Slider
                          value={[design.detailsY]}
                          onValueChange={([v]) => handleDesignChange('detailsY', v)}
                          min={80}
                          max={400}
                          step={5}
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          value={design.detailsY}
                          onChange={(e) => handleDesignChange('detailsY', parseInt(e.target.value) || 0)}
                          className="w-16 h-8 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-sm">Sağ Sütun X Pozisyonu</h4>
                  <div className="flex gap-2 mt-1">
                    <Slider
                      value={[design.detailsRightX]}
                      onValueChange={([v]) => handleDesignChange('detailsRightX', v)}
                      min={300}
                      max={700}
                      step={5}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      value={design.detailsRightX}
                      onChange={(e) => handleDesignChange('detailsRightX', parseInt(e.target.value) || 0)}
                      className="w-16 h-8 text-xs"
                    />
                  </div>
                </div>
                <FontControl label="Detay Başlığı" fontKey="detailsTitleFont" sizeKey="detailsTitleFontSize" colorKey="detailsTitleColor" />
                <FontControl label="Detay Metni" fontKey="detailsFont" sizeKey="detailsFontSize" colorKey="detailsColor" />
              </>
            )}

            {/* İmza */}
            {activeElement === 'signature' && (
              <>
                <PositionControl label="İmza" xKey="signatureX" yKey="signatureY" yMin={20} yMax={550} yLabel="Y (Alttan mesafe)" />
                <FontControl label="İmza İsmi" fontKey="signatureFont" sizeKey="signatureFontSize" colorKey="signatureColor" />
              </>
            )}

            {/* QR Kod */}
            {activeElement === 'qr' && (
              <>
                <PositionControl label="QR Kod" xKey="qrX" yKey="qrY" yMin={20} yMax={550} yLabel="Y (Alttan mesafe)" />
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs">QR Kod Boyutu</Label>
                  <div className="flex gap-2 mt-1">
                    <Slider
                      value={[design.qrSize]}
                      onValueChange={([v]) => handleDesignChange('qrSize', v)}
                      min={30}
                      max={100}
                      step={5}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      value={design.qrSize}
                      onChange={(e) => handleDesignChange('qrSize', parseInt(e.target.value) || 60)}
                      className="w-16 h-8 text-xs"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Disclaimer */}
            {activeElement === 'disclaimer' && (
              <>
                <PositionControl label="Uyarı Metni" xKey="disclaimerX" yKey="disclaimerY" yMin={20} yMax={550} yLabel="Y (Alttan mesafe)" />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Koordinat Bilgisi */}
      <div className="text-xs text-gray-400 text-center">
        Sayfa boyutu: 842 x 595 piksel (A4 Yatay) | Sol üst köşe: (0, 0) | Sağ alt köşe: (842, 595)
      </div>
    </div>
  );
}
